import React from 'react';
import { Box, BoxProps } from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import { HeroSection } from './components/Hero/HeroSection';
import { ShowcaseSection } from './components/Showcase/ShowcaseSection';
import { FaqSection } from './components/Question/Questions';
import {CommentsCards} from './components/Comments/CommentsCards'
import {CompanyList} from './components/Company/CompanyList' 


const useStyles = makeStyles((theme) =>
  createStyles({
    root: {
      minHeight: '100vh',
      backgroundColor: theme.palette.background.default,
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(20),
      paddingBottom: "160px",
      width: '100%',
      overflowX: 'hidden',
      [theme.breakpoints.down(1280)]: {
        gap: theme.spacing(8),
      },
      [theme.breakpoints.down('md')]: {
        paddingBottom: theme.spacing(8),
      },
    },
  })
);

type LocalFadeInSectionProps = BoxProps & {
  delay?: number;
  duration?: number;
  direction?: 'up' | 'down' | 'left' | 'right';
  distance?: number;
};

const FadeInSection: React.FC<LocalFadeInSectionProps> = ({
  children,
  delay = 0,
  duration = 600,
  direction = 'up',
  distance = 160,
  sx,
  ...rest
}) => {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const [isVisible, setIsVisible] = React.useState(false);

  React.useEffect(() => {
    const node = containerRef.current;
    if (!node || isVisible) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [isVisible]);

  const offset = Math.max(distance, 0);
  let hiddenTransform = 'translate3d(0, 0, 0)';
  switch (direction) {
    case 'down':
      hiddenTransform = `translate3d(0, -${offset}px, 0)`;
      break;
    case 'left':
      hiddenTransform = `translate3d(${offset}px, 0, 0)`;
      break;
    case 'right':
      hiddenTransform = `translate3d(-${offset}px, 0, 0)`;
      break;
    case 'up':
    default:
      hiddenTransform = `translate3d(0, ${offset}px, 0)`;
      break;
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translate3d(0, 0, 0)' : hiddenTransform,
        transition: `opacity ${duration}ms ease-out ${delay}ms, transform ${duration}ms ease-out ${delay}ms`,
        willChange: 'opacity, transform',
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Box>
  );
};

export const HomePage: React.FC = () => {
  const classes = useStyles();

  return (
    <>
      <Box component="main" className={classes.root}>
        <FadeInSection>
          <HeroSection />
        </FadeInSection>
        <FadeInSection>
          <ShowcaseSection />
        </FadeInSection>
        <FadeInSection>
          <CommentsCards />
        </FadeInSection>
        <FadeInSection>
          <FaqSection />
        </FadeInSection>
        <FadeInSection>
          <CompanyList />
        </FadeInSection>
      </Box>
    </>
  );
};
